import { useCallback, useEffect, useState } from "react";

type UseConsentManagementReturn = {
  initialised: boolean;
  active: boolean;
};

type ConsentEvent = {
  detail: {
    event: string;
    type: string;
  };
};

export const useConsentManagement = (
  ucId: string,
): UseConsentManagementReturn => {
  const [isInitialised, setIsInitialised] = useState(false);
  const [isActive, setIsActive] = useState(false);

  const isConsentProviderReady = useCallback(() => {
    return window.UC_UI && window.UC_UI.isInitialized();
  }, []);

  const hasConsent = useCallback(
    (ucId: string, skipCheck: boolean = false): boolean => {
      if (!isConsentProviderReady()) {
        return false;
      }

      const consentBasicInfos = window.UC_UI.getServicesBaseInfo();
      const trackerDetails = consentBasicInfos.find((info) => info.id === ucId);

      if (window.UC_UI.isConsentRequired() && !skipCheck) {
        return trackerDetails?.isEssential ?? false;
      }

      return trackerDetails?.consent?.status ?? false;
    },
    [isConsentProviderReady],
  );

  useEffect(() => {
    // Check for consent events, in case it gets activated
    const handleUcEvent = (event: unknown) => {
      if (isConsentProviderReady()) {
        setIsInitialised(true);

        // As UserCentrics handles explicit consent asynchronously but in a weird way,
        // we need to skip the explicit consent check here
        // as UC_UI.isConsentRequired() is true only after this event is handled
        if (
          (event as ConsentEvent).detail.event === "consent_status" &&
          (event as ConsentEvent).detail.type === "explicit"
        ) {
          setIsActive(hasConsent(ucId, true));
        } else {
          setIsActive(hasConsent(ucId));
        }
      }
    };

    const handleUcInitialised = () => {
      setIsInitialised(true);
      setIsActive(hasConsent(ucId));
    };

    window.addEventListener("ucEvent", handleUcEvent);
    window.addEventListener("UC_UI_INITIALIZED", handleUcInitialised);

    // UC UI is already initialised
    if (isConsentProviderReady()) {
      setIsInitialised(true);
      setIsActive(hasConsent(ucId));
    }

    return () => {
      window.removeEventListener("ucEvent", handleUcEvent);
      window.removeEventListener("UC_UI_INITIALIZED", handleUcInitialised);
    };
  }, [hasConsent, isConsentProviderReady, ucId]);

  return {
    initialised: isInitialised,
    active: isActive,
  };
};
